import { ReactNode, useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import Sidebar, { useNavVisible, isActive, navItemStyle } from './Sidebar';
import FontSizeControl from './FontSizeControl';
import { useAuth } from '../context/AuthContext';
import { useConfigNegocio } from '../hooks/useConfigNegocio';
import { aplicarTema, coloresDeConfig } from '../utils/tema';
import api from '../services/api';

const NAV_KEY = 'chargoy.navOpen';
// Con pocas páginas (mesero, cocina de una estación) basta con pestañas arriba;
// el menú lateral es para quien administra muchas áreas.
const MAX_PESTANAS = 5;
const MENU_MIN_ANCHO = 900;
const ROLES: Record<string, string> = {
  admin: 'Administrador', cajero: 'Cajero', mesero: 'Mesero', cocina: 'Cocina', almacenista: 'Almacén',
};

const btnSecundario: React.CSSProperties = {
  height: 48, padding: '0 16px', borderRadius: 10, border: '1px solid var(--line-strong)',
  background: 'var(--surface)', fontSize: 16, fontWeight: 700, color: 'var(--ink)', cursor: 'pointer',
  display: 'flex', alignItems: 'center', gap: 10, flex: 'none',
};

function useTurno(habilitado: boolean) {
  const [turno, setTurno] = useState<{ apertura_at: string } | null | undefined>(undefined);
  useEffect(() => {
    if (!habilitado) return;
    const verificar = () => api.get('/turnos/activo')
      .then(r => setTurno(r.data?.turno ?? null))
      .catch(() => setTurno(undefined));
    verificar();
    const id = setInterval(verificar, 60_000);
    return () => clearInterval(id);
  }, [habilitado]);
  return turno;
}

// Insumos bajo su mínimo (solo quien ve inventario). Se revisa cada 5 min.
const ALERTAS_MS = 300_000;
function useAlertasStock(habilitado: boolean) {
  const [alertas, setAlertas] = useState<any[]>([]);
  useEffect(() => {
    if (!habilitado) return;
    const revisar = () => api.get('/inventario/alertas').then(r => setAlertas(r.data || [])).catch(() => {});
    revisar();
    const id = setInterval(revisar, ALERTAS_MS);
    return () => clearInterval(id);
  }, [habilitado]);
  return alertas;
}

function useEnLinea() {
  const [enLinea, setEnLinea] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setEnLinea(true), off = () => setEnLinea(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  return enLinea;
}

const hora = (iso: string) => new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
const iniciales = (s: string) => s.split(/\s+/).filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2);

export default function Layout({ children }: { children: ReactNode }) {
  const { usuario, logout } = useAuth();
  const config = useConfigNegocio();
  const { pathname } = useLocation();
  const groups = useNavVisible();
  const links = groups.flatMap(g => g.links);
  const usaMenu = links.length > MAX_PESTANAS;
  // En pantallas angostas (tablet vertical, teléfono) el menú arranca plegado:
  // abierto se come media pantalla.
  const [navOpen, setNavOpen] = useState(() => window.innerWidth >= MENU_MIN_ANCHO && localStorage.getItem(NAV_KEY) !== '0');
  const [avisosOpen, setAvisosOpen] = useState(false);
  const esCaja = usuario?.rol === 'admin' || usuario?.rol === 'cajero';
  const turno = useTurno(esCaja);
  const enLinea = useEnLinea();
  const stock = useAlertasStock(usuario?.rol === 'admin');

  useEffect(() => {
    // Ya cargada la config: tema + colores de marca/fondo de cada modo. Sin `tema`
    // guardado es claro (borra una previa que no se guardó).
    if (Object.keys(config).length) aplicarTema(config.tema || 'claro', coloresDeConfig(config));
  }, [config]);

  const toggleNav = () => {
    localStorage.setItem(NAV_KEY, navOpen ? '0' : '1');
    setNavOpen(!navOpen);
  };

  const nombre = config.restaurante || 'CHARGOY';
  const avisos = [
    ...(!enLinea ? [{ t: 'Sin conexión', d: 'Los cambios se envían cuando vuelva la conexión.' }] : []),
    ...(esCaja && turno === null ? [{ t: 'No hay turno de caja abierto', d: 'Ábrelo en Caja › Turno antes de cobrar.' }] : []),
    ...stock.map(i => {
      const disp = +(parseFloat(i.stock_disponible) || 0).toFixed(2);
      return { t: `${disp <= 0 ? 'Agotado' : 'Stock bajo'}: ${i.nombre}`, d: `Quedan ${disp} ${i.unidad} · mínimo ${+parseFloat(i.stock_minimo).toFixed(2)}.` };
    }),
  ];

  return (
    <div style={{ height: 'var(--vh100, 100vh)', display: 'flex', flexDirection: 'column', background: 'var(--bg)', color: 'var(--ink)', fontSize: 16, overflow: 'hidden' }}>
      <header style={{
        minHeight: 68, flex: 'none', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 12px',
        padding: '10px 20px', background: 'var(--surface)', borderBottom: '1px solid var(--line)', position: 'relative', zIndex: 5,
      }}>
        {usaMenu && (
          <button onClick={toggleNav} aria-label="Plegar menú" aria-expanded={navOpen}
            style={{ ...btnSecundario, background: navOpen ? 'var(--well)' : 'var(--surface)' }}>
            <span aria-hidden style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {[0, 1, 2].map(i => <span key={i} style={{ width: 18, height: 2, background: 'currentColor', borderRadius: 2 }} />)}
            </span>
            Menú
          </button>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 44, flex: '0 1 auto' }}>
          {config.logo_base64 ? (
            <img src={config.logo_base64} alt="" style={{ width: 44, height: 44, flex: 'none', borderRadius: 10, objectFit: 'contain', background: '#fff' }} />
          ) : (
            <div style={{ width: 44, height: 44, flex: 'none', borderRadius: 10, background: 'var(--brand)', color: 'var(--on-brand)', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 17 }}>
              {iniciales(nombre)}
            </div>
          )}
          <div style={{ fontWeight: 700, fontSize: 19, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nombre}</div>
        </div>

        {!usaMenu && (
          <nav aria-label="Menú principal" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginLeft: 12 }}>
            {links.map(l => {
              const on = isActive(pathname, l.to);
              return (
                <NavLink key={l.to} to={l.to} className="nav-item" aria-current={on ? 'page' : undefined}
                  style={{ ...navItemStyle(on), height: 48, padding: '0 18px', borderRadius: 10, fontSize: 17, fontWeight: 700 }}>
                  {l.label}
                </NavLink>
              );
            })}
          </nav>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', gap: 10, flex: '1 1 auto', minWidth: 0, marginLeft: 'auto' }}>
          {esCaja && turno !== undefined && (
            <Link to="/turno" style={{
              display: 'flex', alignItems: 'center', gap: 8, height: 40, padding: '0 14px', borderRadius: 999,
              background: turno ? 'var(--libre-bg)' : 'var(--cobrar-bg)', color: turno ? 'var(--libre-fg)' : 'var(--cobrar-fg)',
              fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap', textDecoration: 'none',
            }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: turno ? 'var(--libre-bd)' : 'var(--cobrar-bd)' }} />
              {turno ? `Turno abierto · desde ${hora(turno.apertura_at)}` : 'Sin turno abierto'}
            </Link>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap', color: enLinea ? 'var(--ink-2)' : 'var(--danger)' }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: enLinea ? 'var(--libre-bd)' : 'var(--danger)' }} />
            {enLinea ? 'En línea' : 'Sin conexión'}
          </div>

          <div style={{ position: 'relative' }}>
            <button onClick={() => setAvisosOpen(o => !o)} aria-expanded={avisosOpen} style={btnSecundario}>
              Avisos
              {avisos.length > 0 && (
                <span style={{ minWidth: 24, height: 24, padding: '0 7px', borderRadius: 999, background: 'var(--brand)', color: 'var(--on-brand)', fontSize: 14, display: 'grid', placeItems: 'center' }}>
                  {avisos.length}
                </span>
              )}
            </button>
            {avisosOpen && (
              <div style={{
                position: 'absolute', right: 0, top: 56, width: 340, background: 'var(--surface)', border: '1px solid var(--line)',
                borderRadius: 14, boxShadow: '0 12px 32px rgba(40,30,20,.14)', padding: 8,
              }}>
                {avisos.length === 0 && <div style={{ padding: 12, fontSize: 15, color: 'var(--muted)' }}>Sin avisos por ahora.</div>}
                {avisos.map(a => (
                  <div key={a.t} style={{ padding: 12, borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>{a.t}</div>
                    <div style={{ fontSize: 15, color: 'var(--muted)' }}>{a.d}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <FontSizeControl />

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingLeft: 4 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--well)', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 15 }}>
              {iniciales(usuario?.nombre || 'U')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, whiteSpace: 'nowrap' }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>{usuario?.nombre}</span>
              <span style={{ fontSize: 14, color: 'var(--muted)' }}>{ROLES[usuario?.rol ?? ''] || usuario?.rol}</span>
            </div>
          </div>
          <button onClick={logout} style={btnSecundario}>Salir</button>
        </div>
      </header>

      {!enLinea && (
        <div role="status" style={{ flex: 'none', padding: '12px 20px', background: 'var(--cobrar-bg)', color: 'var(--cobrar-fg)', fontSize: 16, fontWeight: 700, borderBottom: '1px solid var(--cobrar-bd)' }}>
          Sin conexión. Los cambios se enviarán al reconectar.
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {/* Plegado deja el riel de iconos, como el menú anterior. */}
        {usaMenu && <Sidebar groups={groups} compacto={!navOpen} />}
        <main style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '22px 24px' }}>
          {children}
        </main>
      </div>
    </div>
  );
}
